import React, { useEffect, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import { getLatestContractTemplate } from "../api/contractApi";

const TRAINING_TYPE_OPTIONS = [
    "All classes",
    "WOD",
    "Weightlifting",
    "Rowing",
    "Kids",
    "Gymnastics",
    "Open Gym",
    "Cardio",
    "Hyrox",
    "Basic",
    "Canoeing",
];

const DURATION_YEAR_OPTIONS = Array.from({ length: 20 }, (_, index) => index + 1);

const emptyForm = {
    name: "",
    contractType: "",
    content: "",
    trainingTypes: [],
    monthlyPrice: "",
    paymentType: "",
    paymentDayMode: "FLEXIBLE",
    fixedPaymentDays: [],
    durationYears: 1,
    discountMonths: "",
    discountPercent: "",
    additionalInfo: "",
    active: true,
};

const BaseContractModal = ({ open, baseContract, affiliateId, minimumContractValidityMonths, onClose, onSave, onDelete }) => {
    const [form, setForm] = useState(emptyForm);
    const [error, setError] = useState("");
    const [contentError, setContentError] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;

        if (baseContract) {
            setForm({
                ...emptyForm,
                ...baseContract,
                contractType: baseContract.contractType || "",
                paymentType: baseContract.paymentType || "",
                additionalInfo: baseContract.additionalInfo || "",
                monthlyPrice: baseContract.monthlyPrice ?? "",
                durationYears: baseContract.durationYears || 1,
                discountMonths: baseContract.discountMonths || "",
                discountPercent: baseContract.discountPercent || "",
                trainingTypes: Array.isArray(baseContract.trainingTypes) ? baseContract.trainingTypes : [],
                fixedPaymentDays: Array.isArray(baseContract.fixedPaymentDays) ? baseContract.fixedPaymentDays : [],
            });
        } else {
            setForm(emptyForm);
            loadTemplate();
        }

        setError("");
        setContentError(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, baseContract]);

    const loadTemplate = async () => {
        if (!affiliateId) return;
        try {
            const template = await getLatestContractTemplate(affiliateId);
            if (template?.content) {
                setForm((current) => ({ ...current, content: template.content }));
            }
        } catch (templateError) {
            console.error("Error loading contract template:", templateError);
        }
    };

    const setField = (field, value) => {
        setForm((current) => ({ ...current, [field]: value }));
    };

    const validate = () => {
        if (!form.name.trim()) return "Name is required.";
        if (!form.content.trim()) return "Contract content is required.";
        if (form.trainingTypes.length === 0) return "Select at least one training type.";
        if (form.monthlyPrice === "" || Number(form.monthlyPrice) < 0) return "Enter a valid monthly price.";
        if (!DURATION_YEAR_OPTIONS.includes(Number(form.durationYears))) {
            return "Select a contract period between 1 and 20 years.";
        }
        if (form.paymentDayMode === "FIXED" && form.fixedPaymentDays.length === 0) {
            return "Select at least one fixed payment day.";
        }

        const hasDiscountMonths = form.discountMonths !== "" && Number(form.discountMonths) > 0;
        const hasDiscountPercent = form.discountPercent !== "" && Number(form.discountPercent) > 0;
        if (hasDiscountMonths !== hasDiscountPercent) {
            return "Fill in both discount duration and discount percentage, or leave both empty.";
        }
        if (hasDiscountPercent && Number(form.discountPercent) > 100) {
            return "Discount percentage cannot exceed 100.";
        }

        return "";
    };

    const handleSave = async () => {
        setContentError(!form.content.trim());

        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        const payload = {
            name: form.name.trim(),
            contractType: form.contractType.trim() || null,
            content: form.content.trim(),
            trainingTypes: form.trainingTypes,
            monthlyPrice: Number(form.monthlyPrice),
            paymentType: form.paymentType.trim() || null,
            paymentDayMode: form.paymentDayMode,
            fixedPaymentDays: form.paymentDayMode === "FIXED"
                ? [...new Set(form.fixedPaymentDays.map(Number))].sort((a, b) => a - b)
                : [],
            durationYears: Number(form.durationYears),
            discountMonths: form.discountMonths === "" ? 0 : Number(form.discountMonths),
            discountPercent: form.discountPercent === "" ? 0 : Number(form.discountPercent),
            additionalInfo: form.additionalInfo.trim() || null,
            active: form.active,
        };

        setSaving(true);
        setError("");
        try {
            await onSave(payload);
        } catch (saveError) {
            setError(saveError.message || "Could not save base contract.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Delete "${form.name}"?`)) return;
        setSaving(true);
        setError("");
        try {
            await onDelete(baseContract.id);
        } catch (deleteError) {
            setError(deleteError.message || "Could not delete base contract.");
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
            <DialogTitle>{baseContract ? "Edit Base Contract" : "Add Base Contract"}</DialogTitle>
            <DialogContent>
                <Stack spacing={2.5} sx={{ mt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}

                    <Alert severity="info">
                        Members can join a base contract on their own. They choose only the start date and the monthly
                        payment day, accept the terms and pay the first payment. The contract is then created and
                        activated automatically, with no action needed from you.
                    </Alert>

                    <TextField
                        label="Name"
                        value={form.name}
                        onChange={(event) => setField("name", event.target.value)}
                        helperText="Shown to members on the offering card."
                        required
                        fullWidth
                    />

                    <TextField
                        label="Contract type"
                        value={form.contractType}
                        onChange={(event) => setField("contractType", event.target.value)}
                        helperText="Optional. Defaults to the name above."
                        fullWidth
                    />

                    <Autocomplete
                        multiple
                        options={TRAINING_TYPE_OPTIONS}
                        value={form.trainingTypes}
                        onChange={(event, value) => setField("trainingTypes", value)}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip label={option} {...getTagProps({ index })} key={option} />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField {...params} label="Training types" required />
                        )}
                    />

                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <TextField
                            label="Monthly price"
                            type="number"
                            value={form.monthlyPrice}
                            onChange={(event) => setField("monthlyPrice", event.target.value)}
                            inputProps={{ min: 0, step: "0.01" }}
                            required
                            sx={{ flex: "1 1 200px" }}
                        />
                        <FormControl required sx={{ flex: "1 1 200px" }}>
                            <InputLabel>Contract period</InputLabel>
                            <Select
                                value={form.durationYears}
                                label="Contract period"
                                onChange={(event) => setField("durationYears", event.target.value)}
                            >
                                {DURATION_YEAR_OPTIONS.map((years) => (
                                    <MenuItem key={years} value={years}>
                                        {years} {years === 1 ? "year" : "years"}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <TextField
                        label="Payment description"
                        value={form.paymentType}
                        onChange={(event) => setField("paymentType", event.target.value)}
                        helperText="Optional. Appears on the member's payment."
                        fullWidth
                    />

                    <FormControl fullWidth>
                        <InputLabel>Payment day mode</InputLabel>
                        <Select
                            value={form.paymentDayMode}
                            label="Payment day mode"
                            onChange={(event) => setField("paymentDayMode", event.target.value)}
                        >
                            <MenuItem value="FLEXIBLE">Customer chooses</MenuItem>
                            <MenuItem value="FIXED">Fixed options</MenuItem>
                        </Select>
                    </FormControl>

                    {form.paymentDayMode === "FIXED" && (
                        <FormControl fullWidth>
                            <InputLabel>Fixed payment days</InputLabel>
                            <Select
                                multiple
                                value={form.fixedPaymentDays}
                                label="Fixed payment days"
                                onChange={(event) => setField("fixedPaymentDays", event.target.value)}
                                renderValue={(days) => [...days].sort((a, b) => a - b).join(", ")}
                            >
                                {Array.from({ length: 28 }, (_, index) => index + 1).map((day) => (
                                    <MenuItem key={day} value={day}>{day}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <TextField
                            label="Discount duration (payments)"
                            type="number"
                            value={form.discountMonths}
                            onChange={(event) => setField("discountMonths", event.target.value)}
                            inputProps={{ min: 0, step: 1 }}
                            sx={{ flex: "1 1 200px" }}
                        />
                        <TextField
                            label="Discount percentage (%)"
                            type="number"
                            value={form.discountPercent}
                            onChange={(event) => setField("discountPercent", event.target.value)}
                            inputProps={{ min: 0, max: 100, step: "0.01" }}
                            sx={{ flex: "1 1 200px" }}
                        />
                    </Box>

                    <Alert severity="info">
                        Members must keep this contract active for at least{" "}
                        <strong>
                            {minimumContractValidityMonths} {minimumContractValidityMonths === 1 ? "month" : "months"}
                        </strong>{" "}
                        before they can request termination. This is a club-wide setting and is changed on the
                        Contracts page.
                    </Alert>

                    <TextField
                        label="Additional information"
                        value={form.additionalInfo}
                        onChange={(event) => setField("additionalInfo", event.target.value)}
                        multiline
                        minRows={3}
                        helperText="Shown on the offering card, above the contract text."
                        fullWidth
                    />

                    <Divider />

                    <Box>
                        <Typography variant="h6" gutterBottom>Contract text</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            This is the binding contract the member reads and agrees to. It is copied onto every
                            contract created from this base contract, so later edits here do not change contracts that
                            have already been signed.
                        </Typography>
                        <FormControl fullWidth error={contentError}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Contract Content *</Typography>
                            <TextareaAutosize
                                minRows={10}
                                value={form.content}
                                onChange={(event) => {
                                    setField("content", event.target.value);
                                    setContentError(false);
                                }}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    borderColor: contentError ? '#d32f2f' : '#AAAAAA',
                                    fontSize: '14px',
                                    lineHeight: '1.5',
                                    resize: 'vertical',
                                    fontFamily: 'inherit'
                                }}
                            />
                            {contentError && (
                                <FormHelperText error>Contract content is required</FormHelperText>
                            )}
                        </FormControl>
                    </Box>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={form.active}
                                onChange={(event) => setField("active", event.target.checked)}
                            />
                        }
                        label="Active (visible to members)"
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                {baseContract && (
                    <Button color="error" onClick={handleDelete} disabled={saving} sx={{ mr: "auto" }}>
                        Delete
                    </Button>
                )}
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BaseContractModal;
